import { Controller } from "@hotwired/stimulus";
import { formatDate, getJSON, listResults } from "../api.js";

// Panel „Kupione leady" personelu. Wymaga uprawnienia core.view_lead_pii — bez niego
// endpoint /api/staff/purchased/ zwraca 403. Przy braku uprawnień pokazujemy baner
// i nie wykonujemy żadnego zapytania. Renderujemy wyłącznie dane transakcyjne
// (lead_code, kategoria, wertykał, org, kupujący-email, daty) — nie dane kontaktowe leada.
export default class extends Controller {
  static targets = [
    "banner",
    "verticalFilter",
    "paidFrom",
    "paidTo",
    "rows",
    "status",
    "pagination",
    "pageInfo",
    "prevBtn",
    "nextBtn",
  ];
  static values = {
    url: String,       // /api/staff/purchased/
    canViewPii: Boolean, // core.view_lead_pii
  };

  static DELIVERY_LABEL = {
    pending: "Oczekująca",
    ready: "Zbudowana",
    delivered: "Dostarczona",
    failed: "Nieudana",
  };

  connect() {
    if (this.hasBannerTarget) this.bannerTarget.hidden = this.canViewPiiValue;
    this.page = 1;
    if (!this.canViewPiiValue) {
      this.#status("Brak dostępu do kupionych leadów.");
      return;
    }
    this.load();
  }

  applyFilters() {
    this.page = 1;
    this.load();
  }

  prevPage() {
    if (this.page > 1) {
      this.page -= 1;
      this.load();
    }
  }

  nextPage() {
    if (this.hasNextPage) {
      this.page += 1;
      this.load();
    }
  }

  async load() {
    let data;
    try {
      data = await getJSON(this.#listUrl());
    } catch {
      this.#status("Nie udało się załadować kupionych leadów. Odśwież stronę.");
      return;
    }
    const results = listResults(data);
    this.rowsTarget.textContent = "";
    this.#renderPagination(data);
    if (!results.length) {
      this.#status("Brak kupionych leadów.");
      return;
    }
    this.#status(null);
    const template = document.getElementById("purchased-admin-row-template");
    for (const lead of results) {
      const row = template.content.firstElementChild.cloneNode(true);

      const leadCell = row.querySelector('[data-field="lead"]');
      if (leadCell) leadCell.textContent = lead.lead_code;

      const categoryCell = row.querySelector('[data-field="category"]');
      if (categoryCell) {
        const parts = [lead.category_name, lead.subcategory_name].filter(Boolean);
        categoryCell.textContent = parts.length ? parts.join(" / ") : "—";
      }

      const verticalCell = row.querySelector('[data-field="vertical"]');
      if (verticalCell) {
        verticalCell.textContent = lead.vertical_name || lead.vertical_slug || "—";
      }

      const orgCell = row.querySelector('[data-field="org"]');
      if (orgCell) orgCell.textContent = lead.organization_name || "—";

      const buyerCell = row.querySelector('[data-field="buyer"]');
      if (buyerCell) buyerCell.textContent = lead.buyer || "—";

      const paidCell = row.querySelector('[data-field="paid"]');
      if (paidCell) {
        paidCell.textContent = lead.paid_at ? formatDate(lead.paid_at) : "—";
      }

      const backendCell = row.querySelector('[data-field="backend"]');
      if (backendCell) backendCell.textContent = lead.payment_backend || "—";

      const deliveryCell = row.querySelector('[data-field="delivery"]');
      if (deliveryCell) {
        const raw = lead.delivery_status;
        deliveryCell.textContent =
          raw != null
            ? (this.constructor.DELIVERY_LABEL[raw] ?? raw)
            : "—";
      }

      this.rowsTarget.appendChild(row);
    }
  }

  // --- prywatne ---

  #listUrl() {
    const query = new URLSearchParams();
    if (this.page > 1) query.set("page", this.page);
    const vertical = this.hasVerticalFilterTarget
      ? this.verticalFilterTarget.value
      : "";
    if (vertical) query.set("vertical", vertical);
    const paidFrom = this.hasPaidFromTarget ? this.paidFromTarget.value : "";
    if (paidFrom) query.set("paid_from", paidFrom);
    const paidTo = this.hasPaidToTarget ? this.paidToTarget.value : "";
    if (paidTo) query.set("paid_to", paidTo);
    const text = query.toString();
    return text ? `${this.urlValue}?${text}` : this.urlValue;
  }

  #renderPagination(data) {
    this.hasNextPage = Boolean(data.next);
    const hasPrev = Boolean(data.previous);
    if (this.hasPageInfoTarget) {
      const count = data.count ?? listResults(data).length;
      this.pageInfoTarget.textContent = `strona ${this.page} (${count} leadów)`;
    }
    if (this.hasPrevBtnTarget) this.prevBtnTarget.disabled = !hasPrev;
    if (this.hasNextBtnTarget) this.nextBtnTarget.disabled = !this.hasNextPage;
    if (this.hasPaginationTarget) {
      this.paginationTarget.hidden = !(this.hasNextPage || hasPrev);
    }
  }

  #status(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.hidden = message === null;
    if (message !== null) this.statusTarget.textContent = message;
  }
}
